import { type Predicate, type SearchDescriptor, toSearchRow } from '@shared/core/search';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { AgAutocomplete } from 'src/components/ui/AgAutocomplete/AgAutocomplete';

import { useSearch } from '../hooks/useSearch';
import { useSearchCredentials } from '../providers/SearchKeyProvider';

export interface EntityPickerProps<TDoc> {
  readonly descriptor: SearchDescriptor<TDoc>;
  readonly filter?: Predicate<TDoc>;
  readonly value: number | null;
  readonly onChange: (id: number | null, doc: TDoc | null) => void;
  readonly getLabel: (doc: TDoc) => string;
  readonly getId?: (doc: TDoc) => number | null;
  readonly label?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly selectedLabel?: string;
  readonly emptyText?: ReactNode;
  readonly isRequired?: boolean;
  readonly autoFocus?: boolean;
  readonly selectOnEnter?: boolean;
  readonly selectOnTab?: boolean;
  readonly helperText?: string;
  readonly className?: string;
  readonly excludeIds?: readonly number[];
  readonly renderOption?: (doc: TDoc) => ReactNode;
  readonly 'data-testid'?: string;
}

interface Option<TDoc> {
  readonly id: number;
  readonly label: string;
  readonly doc: TDoc | null;
}

const NO_OPTIONS: never[] = [];

const rowId = (entity: string, doc: unknown): number | null => {
  try {
    return toSearchRow[entity]?.(doc).id ?? null;
  } catch {
    return null;
  }
};

export function EntityPicker<TDoc extends object>({
  descriptor,
  filter,
  value,
  onChange,
  getLabel,
  getId,
  label,
  placeholder,
  disabled,
  selectedLabel,
  emptyText = 'No matches',
  isRequired,
  autoFocus,
  selectOnEnter,
  selectOnTab,
  helperText,
  className = 'w-full',
  excludeIds,
  renderOption,
  'data-testid': testId,
}: EntityPickerProps<TDoc>) {
  const [query, setQuery] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [selected, setSelected] = useState<Option<TDoc> | null>(null);
  const { refresh } = useSearchCredentials();
  const { hits, state } = useSearch({ descriptor, query, filter, retryKey });

  const options = useMemo(
    () =>
      state === 'forbidden'
        ? NO_OPTIONS
        : hits.flatMap((doc) => {
            const id = getId ? getId(doc) : rowId(descriptor.entity, doc);
            return id === null || excludeIds?.includes(id) ? [] : [{ id, label: getLabel(doc), doc }];
          }),
    [hits, state, descriptor.entity, getLabel, getId, excludeIds]
  );

  const current = useMemo(() => {
    if (value === null) return null;
    const known = options.find((option) => option.id === value) ?? (selected?.id === value ? selected : null);
    if (known) return known;
    return selectedLabel === undefined ? null : { id: value, label: selectedLabel, doc: null };
  }, [options, selected, selectedLabel, value]);

  const retry = useCallback(() => {
    setRetryKey((key) => key + 1);
    void refresh();
  }, [refresh]);

  const handleChange = useCallback(
    (option: Option<TDoc> | null) => {
      setSelected(option);
      if (option === null) onChange(null, null);
      else onChange(option.id, option.doc);
    },
    [onChange]
  );

  const renderPickerOption = useMemo(
    () =>
      renderOption
        ? (option: Option<TDoc>): ReactNode => (option.doc === null ? option.label : renderOption(option.doc))
        : undefined,
    [renderOption]
  );

  const noOptionsText: ReactNode =
    state === 'unavailable' ? (
      <span className="flex items-center gap-2">
        <span>Search is unavailable — try again</span>
        <button
          type="button"
          className="text-brand-text underline"
          onPointerDown={(event) => event.preventDefault()}
          onClick={retry}
        >
          Retry
        </button>
      </span>
    ) : state === 'forbidden' ? (
      'Not available for your role'
    ) : (
      emptyText
    );

  return (
    <div data-testid={testId} data-state={state} className={className}>
      <AgAutocomplete<Option<TDoc>>
        options={options}
        value={current}
        onChange={handleChange}
        onInputChange={setQuery}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option, other) => option.id === other.id}
        label={label}
        isRequired={isRequired}
        autoFocus={autoFocus}
        selectOnEnter={selectOnEnter}
        selectOnTab={selectOnTab}
        helperText={helperText}
        renderOption={renderPickerOption}
        placeholder={placeholder}
        aria-label={label === undefined ? (placeholder ?? descriptor.entity) : undefined}
        isDisabled={disabled}
        isLoading={state === 'loading'}
        filterOptions={false}
        noOptionsText={noOptionsText}
      />
    </div>
  );
}
